import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { resetOpsCache } from "@/lib/stubs";
import type { LoginResult, Me } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { TwoStepForm } from "./TwoStepForm";

const MIN_PASSWORD = 12;

interface LinkInfo {
  email: string;
  name: string;
  expires: string;
}

/** Reads the set-up token from the URL fragment once, then clears it from the address bar. */
function takeToken(): string {
  const tok = window.location.hash.replace(/^#/, "").trim();
  if (tok) window.history.replaceState(null, "", window.location.pathname);
  return tok;
}

/**
 * The page behind an invite link (users_invite): <base>/setup#<token>. The person picks a
 * password, then sets up two-step sign-in exactly like a first sign-in.
 */
export function SetupPage() {
  const [token] = useState(takeToken);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [gone, setGone] = useState(token ? "" : "This page needs the set-up link from your invite. Open the link again.");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [step, setStep] = useState<LoginResult | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    api<LinkInfo>("/api/v1/auth/setup/check", { json: { token } }).then(setInfo, (err) =>
      setGone(err instanceof ApiError ? err.message : "This set-up link can't be checked right now. Try again in a minute."),
    );
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
    if (password !== again) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      setStep(await api<LoginResult>("/api/v1/auth/setup", { json: { token, password } }));
      setPassword("");
      setAgain("");
    } catch (err) {
      if (err instanceof ApiError && err.code === "not_found") setGone(err.message);
      else setError(err instanceof ApiError ? err.message : "Set-up failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const done = (user: Me) => {
    resetOpsCache();
    void qc.invalidateQueries({ queryKey: ["has-op"] });
    qc.setQueryData(["me"], user);
    navigate({ to: "/" });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="space-y-4 py-6">
          <div>
            <h1 className="text-lg font-semibold">CREU CRM</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">{step ? "Two-step verification" : "Set up your account"}</p>
          </div>
          {gone && !step && (
            <p role="alert" className="text-sm">
              {gone}{" "}
              <a href="/login" className="underline">
                Go to sign-in
              </a>
            </p>
          )}
          {!gone && !info && !step && <p className="text-sm text-[var(--color-muted-foreground)]">Checking your link…</p>}
          {!gone && info && !step && (
            <form onSubmit={submit} className="space-y-3">
              <p className="text-sm">
                Welcome, {info.name}. You'll sign in as <strong>{info.email}</strong>. This link works until {info.expires}.
              </p>
              <input type="email" autoComplete="username" value={info.email} readOnly hidden />
              <div className="space-y-1">
                <Label htmlFor="new-password">New password</Label>
                <Input id="new-password" type="password" autoComplete="new-password" minLength={MIN_PASSWORD} value={password} onChange={(e) => setPassword(e.target.value)} required />
                <p className="text-xs text-[var(--color-muted-foreground)]">{MIN_PASSWORD} or more characters. A passphrase of a few words works well.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="again">Type it again</Label>
                <Input id="again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                Continue
              </Button>
              {error && (
                <p role="alert" className="text-sm text-[var(--color-destructive)]">
                  {error}
                </p>
              )}
            </form>
          )}
          {step && <TwoStepForm step={step} onDone={done} onExpired={() => navigate({ to: "/login", search: { next: "/" } })} />}
        </CardContent>
      </Card>
    </div>
  );
}
